import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';

import { BACKEND_URL } from '../utils/api';

const TXT = {
  en: {
    inlineTitle: 'Stay Updated with Web-TechServices',
    inlineSub: 'Get exclusive tips, industry insights, and special offers delivered to your inbox.',
    emailPh: 'Enter your email',
    subscribe: 'Subscribe',
    subscribing: 'Subscribing...',
    successInline: 'Successfully subscribed! Check your email.',
    footerTitle: 'Newsletter',
    footerSub: 'Get weekly tips and updates on web development, SEO, and digital marketing.',
    footerEmailPh: 'Your email',
    successFooter: 'Thanks for subscribing!',
    bookConsult: 'Book Free Consultation',
    errInvalidEmail: 'Please enter a valid email address',
    errNetwork: 'Network error. Please try again.',
    errGeneric: 'Failed to subscribe. Please try again.',
  },
  es: {
    inlineTitle: 'Mantente al día con Web-TechServices',
    inlineSub: 'Recibe consejos exclusivos, información del sector y ofertas especiales en tu bandeja de entrada.',
    emailPh: 'Ingresa tu correo',
    subscribe: 'Suscribirme',
    subscribing: 'Suscribiendo...',
    successInline: '¡Suscripción exitosa! Revisa tu correo.',
    footerTitle: 'Boletín',
    footerSub: 'Recibe consejos semanales y novedades sobre desarrollo web, SEO y marketing digital.',
    footerEmailPh: 'Tu correo',
    successFooter: '¡Gracias por suscribirte!',
    bookConsult: 'Reservar Consulta Gratis',
    errInvalidEmail: 'Por favor ingresa un correo válido',
    errNetwork: 'Error de red. Por favor inténtalo de nuevo.',
    errGeneric: 'No se pudo suscribir. Por favor inténtalo de nuevo.',
  },
};

const NewsletterSignup = ({ variant = 'inline' }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  const tx = TXT[lang];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError(tx.errInvalidEmail);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || '',
          subscribed_at: new Date().toISOString(),
          source: variant,
          preferred_language: lang,
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        setEmail('');
        setName('');
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(tx.errGeneric);
      }
    } catch (err) {
      setError(tx.errNetwork);
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === 'inline') {
    return (
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="h-12 w-12 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">{tx.inlineTitle}</h3>
          <p className="text-white/90 mb-6">
            {tx.inlineSub}
          </p>
          
          {success ? (
            <div className="bg-green-500 text-white px-6 py-4 rounded-lg flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">{tx.successInline}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <Input
                type="email"
                placeholder={tx.emailPh}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white text-gray-900"
                required
              />
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-white text-indigo-600 hover:bg-gray-100 font-semibold"
              >
                {submitting ? tx.subscribing : tx.subscribe}
              </Button>
            </form>
          )}
          {error && <p className="text-red-200 text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className="max-w-md">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5" />
          {tx.footerTitle}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {tx.footerSub}
        </p>
        
        {success ? (
          <div className="bg-green-600 text-white px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {tx.successFooter}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <Input
              type="email"
              placeholder={tx.footerEmailPh}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              required
            />
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              size="sm"
            >
              {submitting ? tx.subscribing : tx.subscribe}
            </Button>
            <a href="/book-consultation" className="block">
              <Button 
                type="button"
                variant="outline"
                className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10 mt-1"
                size="sm"
              >
                {tx.bookConsult}
              </Button>
            </a>
          </form>
        )}
        {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
      </div>
    );
  }

  return null;
};

export default NewsletterSignup;
